import { WILDCARD, type RoleGrantSummary } from '@/models/permission';
import { type StudioMemberState } from '@/models/user';
import { type Sort } from '@/utils/sort';

import { type GrantScope } from '../api/useRoleGrants';
import {
  type InvitationMove,
  type MembershipMove,
} from '../api/useStudioMembers';

/**
 * URL-backed list filters, mirroring the endpoint's own query params. Applied by
 * the backend, never in the browser: the list is paginated, so filtering the page
 * in hand would search a slice and report the rest as absent.
 */
export interface MemberFilters extends Sort<MemberSortColumn> {
  /**
   * Case-insensitive **prefix** match on display_name or email — not a substring
   * search, and it does not look at login. That is the SQL admin-api runs
   * (user_repository.go).
   */
  search?: string;
  /** A Role's id, or `none` for the members holding no Role in the Studio. */
  role_id?: string;
}

/** The columns the table sorts on; admin-api also takes email and created_at. */
export type MemberSortColumn = 'display_name' | 'last_login';

/** admin-api's default sort column for the members. */
export const MEMBER_SORT_FALLBACK: MemberSortColumn = 'display_name';

/** `role_id` asking for the members holding no Role (admin-api's own value). */
export const NO_ROLE = 'none';

// Whether a Grant covers every Sandbox — what the Layers icon stands for, as
// opposed to the Box of a single Sandbox, and the only reason to highlight a scope
// as broader than the others.
//
// Deliberately blind to game_code: the caller has already established which Game is
// in view — a Game route, or the Game tab in "My account" — so a Nacon-wide
// ('*', 'SDB001') Grant confers exactly SDB001 there and must read like any other
// single-Sandbox Grant. A predicate that also fired on a wildcard game_code got the
// label, the icon and the highlight to disagree.
//
// Typed on the one field they read, so both the viewer's own Grants and another
// user's can use them.
export const isAllSandboxes = ({ sandbox_code }: { sandbox_code: string }) =>
  sandbox_code === WILDCARD;

// A ('*', '*') Grant means every Sandbox of the Game in view; naming the Game is the
// caller's job, not this label's.
export const scopeLabel = (grant: { sandbox_code: string }) =>
  isAllSandboxes(grant) ? 'All Sandboxes' : grant.sandbox_code;

/**
 * The Game column, read like the Sandbox one: All Games for a '*' Game, whether
 * Studio-wide or global — the sheet already sits in one Studio.
 */
export const gameLabel = (grant: { game_code: string }) =>
  grant.game_code === WILDCARD ? 'All Games' : grant.game_code;

// Seen from a Studio, the Game is no longer implied: (S, *, *) reads as every Game
// of the Studio, anything narrower names its Game first.
export const studioScopeLabel = (grant: {
  game_code: string;
  sandbox_code: string;
}) =>
  grant.game_code === WILDCARD
    ? 'All Games'
    : `${grant.game_code} · ${scopeLabel(grant)}`;

/**
 * The natural key of a Grant. A Role can be granted once per scope, so this is
 * unique per user — the React key of every Grant row, and the id the table
 * selection tracks.
 */
export const grantKey = ({
  id,
  studio_code,
  game_code,
  sandbox_code,
}: RoleGrantSummary) => `${id}-${studio_code}-${game_code}-${sandbox_code}`;

/** The revoke payload: a Grant's `id` is its Role's, sent back as `role_id`. */
export const grantsToScopes = (grants: RoleGrantSummary[]): GrantScope[] =>
  grants.map(({ id, studio_code, game_code, sandbox_code }) => ({
    role_id: id,
    studio_code,
    game_code,
    sandbox_code,
  }));

export const roleLabel = (role: string) =>
  role.replace(/([a-z])([A-Z])/g, '$1 $2');

export const memberStateLabel: Record<StudioMemberState, string> = {
  active: 'Active',
  deactivated: 'Deactivated',
  pending: 'Pending',
  cancelled: 'Cancelled',
  expired: 'Expired',
};

/**
 * The invitation moves admin-api allows from a state (noop#362): a standing or
 * lapsed invitation can be cancelled, a lapsed or cancelled one sent again. A
 * standing one is not resent — it is still running.
 */
export const invitationMoves = (state: StudioMemberState): InvitationMove[] => [
  ...(state === 'pending' || state === 'expired' ? (['cancel'] as const) : []),
  ...(state === 'expired' || state === 'cancelled'
    ? (['resend'] as const)
    : []),
];

/**
 * The membership moves admin-api allows from a state (noop#363): an active
 * member is deactivated, a deactivated one reactivated. A pending invitation is
 * cancelled rather than deactivated.
 */
export const membershipMoves = (state: StudioMemberState): MembershipMove[] =>
  state === 'active'
    ? ['deactivate']
    : state === 'deactivated'
      ? ['reactivate']
      : [];
